import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Mail,
  Send,
  Users,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  X,
  FileText,
  Paperclip,
  Image,
  Trash2,
  Search,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Header from "../../components/Header";
import ConfirmDialog from "../../components/admin/ConfirmDialog";
import { useLocation } from "react-router-dom";
import adminService from "../../api/services/admin.service";
import emailTemplatesService from "../../api/services/emailTemplates.service";
import { BUILTIN_EMAIL_TEMPLATES } from "../../data/emailTemplates";
import toast from "react-hot-toast";

import { recipientFields, regardingPreview } from "./massEmailRecipients";
import { toEmailHtml } from "../../utils/emailHtml";
import { EMAIL_BRANDS } from "../../constants/company";
import { sortClassesByName } from "../../utils/format";
import ScheduleEditor from "../../components/admin/ScheduleEditor";
import {
  defaultSchedule,
  describeSchedule,
  scheduleError,
  scheduleFields,
} from "./emailSchedule";
const RECIPIENT_TYPES = [
  {
    value: "all",
    label: "All Parents",
    description: "Parents with active enrollments",
  },
  {
    value: "all_accounts",
    label: "All Accounts",
    description: "Everyone who created an account",
  },
  {
    value: "class",
    label: "By Class",
    description: "Send to parents of a specific class",
  },
  {
    value: "program",
    label: "By Program",
    description: "Send to parents in a program, optionally one area",
  },
  {
    value: "area",
    label: "By Area",
    description: "Send to parents in an area, optionally one program",
  },
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_TOTAL_SIZE = 30 * 1024 * 1024; // 30 MB
const MAX_FILE_COUNT = 5;
const ALLOWED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const ALLOWED_INLINE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MassEmail() {
  const [recipientType, setRecipientType] = useState("all");
  // Several classes at once: one announcement usually covers every session
  // at a site, and hunting them down one at a time was the slow part.
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [classSearch, setClassSearch] = useState("");
  const [classFilter, setClassFilter] = useState(""); // "", active, upcoming, completed
  // Preselected single recipient (arriving from Users/Clients email click)
  const [customRecipient, setCustomRecipient] = useState(null);
  const location = useLocation();

  // Email templates: 5 built-ins + admin-saved custom ones
  const [savedTemplates, setSavedTemplates] = useState([]);
  // Email appearance (header label, header color, standard closing lines)
  const [headerSubtitle, setHeaderSubtitle] = useState("Announcement");
  const [headerAccent, setHeaderAccent] = useState("#173151");
  const [includeClosing, setIncludeClosing] = useState(true);
  const [brand, setBrand] = useState("csf");
  const sender = EMAIL_BRANDS[brand] || EMAIL_BRANDS.csf;
  const [activeTemplateId, setActiveTemplateId] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    emailTemplatesService
      .list()
      .then((items) => {
        if (!cancelled) setSavedTemplates(items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject || "");
    setMessage(tpl.html || tpl.body_html || "");
    setActiveTemplateId(tpl.id);
    setHeaderSubtitle(tpl.headerSubtitle || "Announcement");
    setHeaderAccent(tpl.accent || "#173151");
    setIncludeClosing(tpl.includeClosing !== undefined ? tpl.includeClosing : true);
    setBrand(tpl.brand || "csf");
  };

  const clearTemplate = () => {
    setSubject("");
    setMessage("");
    setActiveTemplateId("");
    setHeaderSubtitle("Announcement");
    setHeaderAccent("#173151");
    setIncludeClosing(true);
    setBrand("csf");
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Give the template a name");
      return;
    }
    if (!subject.trim() || !hasMessageContent) {
      toast.error("Write a subject and message first");
      return;
    }
    setSavingTemplate(true);
    try {
      const created = await emailTemplatesService.create({
        name: templateName.trim(),
        subject: subject.trim(),
        body_html: message,
      });
      setSavedTemplates((prev) => [created, ...prev]);
      setActiveTemplateId(created.id);
      setTemplateName("");
      setShowSaveTemplate(false);
      toast.success("Template saved");
    } catch (e) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await emailTemplatesService.remove(id);
      setSavedTemplates((prev) => prev.filter((t) => t.id !== id));
      if (activeTemplateId === id) setActiveTemplateId("");
      toast.success("Template deleted");
    } catch (e) {
      toast.error("Failed to delete template");
    }
  };

  useEffect(() => {
    const r = location.state?.recipient;
    if (r?.id && r?.email) {
      setCustomRecipient(r);
      setRecipientType("custom");
    }
  }, [location.state]);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  // Optional narrowing: one program in one area. Kept per mode.
  const [programAreaId, setProgramAreaId] = useState("");
  const [areaProgramId, setAreaProgramId] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  // "now" sends on confirm; "later" schedules it (once or on repeat).
  const [sendMode, setSendMode] = useState("now");
  const [schedule, setSchedule] = useState(() => defaultSchedule());
  const scheduleProblem = sendMode === "later" ? scheduleError(schedule) : null;
  const [sendResult, setSendResult] = useState(null);

  // Data for dropdowns
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [showPreview, setShowPreview] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [inlineImages, setInlineImages] = useState([]);
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);

  const totalFileSize = useMemo(() => {
    const attSize = attachments.reduce((sum, f) => sum + f.size, 0);
    const imgSize = inlineImages.reduce((sum, f) => sum + f.size, 0);
    return attSize + imgSize;
  }, [attachments, inlineImages]);

  const totalFileCount = attachments.length + inlineImages.length;

  // react-quill v2 requires `modules` to be a stable reference for the lifetime
  // of the editor. If it changes post-mount the editor re-instantiates and the
  // message body disappears. We mirror the current limits into refs so the
  // (stable) image handler below can read fresh values without invalidating
  // its identity.
  const totalFileSizeRef = useRef(0);
  const totalFileCountRef = useRef(0);
  useEffect(() => {
    totalFileSizeRef.current = totalFileSize;
    totalFileCountRef.current = totalFileCount;
  }, [totalFileSize, totalFileCount]);

  // Quill image handler for inline images. Stable across renders.
  const handleQuillImage = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg,image/png,image/gif";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!ALLOWED_INLINE_IMAGE_TYPES.includes(file.type)) {
        toast.error("Only JPG, PNG, and GIF images are allowed");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Image exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`);
        return;
      }
      if (totalFileSizeRef.current + file.size > MAX_TOTAL_SIZE) {
        toast.error(
          `Total file size would exceed ${MAX_TOTAL_SIZE / (1024 * 1024)}MB limit`,
        );
        return;
      }
      if (totalFileCountRef.current + 1 > MAX_FILE_COUNT) {
        toast.error(`Maximum ${MAX_FILE_COUNT} files allowed`);
        return;
      }

      const contentId = `img-${crypto.randomUUID()}`;
      const newImage = {
        id: contentId,
        file,
        name: file.name,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
      };
      setInlineImages((prev) => [...prev, newImage]);

      // Insert CID image tag into editor
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, "image", `cid:${contentId}`);
        quill.setSelection(range.index + 1);
      }
    };
    input.click();
  }, []);

  // Quill editor toolbar configuration — MUST stay stable across renders.
  // `handleQuillImage` is intentionally stable (defined with empty deps above),
  // so omitting it from the dep array is correct; including it would defeat
  // the point and re-instantiate the editor on every attachment change.
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link", "image"],
          ["clean"],
        ],
        handlers: {
          image: handleQuillImage,
        },
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "align",
    "link",
    "image",
  ];

  const handleAttachmentAdd = (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" — file type not allowed`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          `"${file.name}" exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`,
        );
        continue;
      }
      if (totalFileSize + file.size > MAX_TOTAL_SIZE) {
        toast.error(
          `Adding "${file.name}" would exceed ${MAX_TOTAL_SIZE / (1024 * 1024)}MB total limit`,
        );
        continue;
      }
      if (totalFileCount + 1 > MAX_FILE_COUNT) {
        toast.error(`Maximum ${MAX_FILE_COUNT} files allowed`);
        break;
      }
      setAttachments((prev) => [
        ...prev,
        { id: crypto.randomUUID(), file, name: file.name, size: file.size },
      ]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((f) => f.id !== id));
  };

  const removeInlineImage = (id) => {
    setInlineImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img?.previewUrl) URL.revokeObjectURL(img.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  };

  // Check if message has actual content (not just empty tags)
  const hasMessageContent = useMemo(() => {
    if (!message) return false;
    const stripped = message.replace(/<[^>]*>/g, "").trim();
    return stripped.length > 0;
  }, [message]);

  // Refetch only classes when the lifecycle filter changes (skip mount —
  // the initial loader above already fetched the unfiltered list).
  const classFilterMounted = useRef(false);
  useEffect(() => {
    if (!classFilterMounted.current) {
      classFilterMounted.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const m = await import("../../api/services/classes.service");
        const res = await m.default.getAll(
          classFilter ? { lifecycle: classFilter } : {},
        );
        if (!cancelled) setClasses(sortClassesByName(res?.items || res || []));
      } catch {
        // keep the previous list on failure
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classFilter]);

  // Fetch classes, programs, areas for dropdown selection
  useEffect(() => {
    const fetchOptions = async () => {
      setLoadingOptions(true);
      try {
        const [classesRes, programsRes, areasRes] = await Promise.allSettled([
          import("../../api/services/classes.service").then((m) =>
            m.default.getAll(),
          ),
          import("../../api/services/programs.service").then((m) =>
            m.default.getAll(),
          ),
          import("../../api/services/areas.service").then((m) =>
            m.default.getAll(),
          ),
        ]);

        if (classesRes.status === "fulfilled") {
          setClasses(
            sortClassesByName(classesRes.value?.items || classesRes.value || []),
          );
        }
        // eslint-disable-next-line no-empty
        if (programsRes.status === "fulfilled") {
          setPrograms(programsRes.value?.items || programsRes.value || []);
        }
        if (areasRes.status === "fulfilled") {
          setAreas(areasRes.value?.items || areasRes.value || []);
        }
      } catch {
        // Silently fail - dropdowns will be empty
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchOptions();
  }, []);

  const validate = () => {
    if (!subject.trim()) {
      toast.error("Subject is required");
      return false;
    }
    if (!hasMessageContent) {
      toast.error("Message is required");
      return false;
    }
    if (recipientType === "class" && selectedClassIds.length === 0) {
      toast.error("Please select at least one class");
      return false;
    }
    if (recipientType === "program" && !selectedProgramId) {
      toast.error("Please select a program");
      return false;
    }
    if (recipientType === "custom" && !customRecipient?.id) {
      toast.error("No recipient selected");
      return;
    }
    if (recipientType === "area" && !selectedAreaId) {
      toast.error("Please select an area");
      return false;
    }
    return true;
  };

  const handleSend = async () => {
    if (!validate()) return;

    if (sendMode === "later") {
      if (scheduleProblem) return;
      setConfirmDialog({
        isOpen: true,
        title: "Schedule Mass Email",
        message: `Schedule this email to ${getRecipientLabel()}: ${describeSchedule(schedule)} (Eastern Time)? Recipients are worked out when it goes.`,
        action: confirmSchedule,
      });
      return;
    }
    setConfirmDialog({
      isOpen: true,
      title: "Send Mass Email",
      message: `Are you sure you want to send this email to ${getRecipientLabel()}? This action cannot be undone.`,
      action: confirmSend,
    });
  };

  // The email exactly as Send now posts it; scheduling adds when it goes.
  const buildEmailForm = () => {
    const formData = new FormData();
    formData.append("recipient_type", recipientType);
    formData.append("subject", subject.trim());
    formData.append("header_subtitle", headerSubtitle.trim() || "Announcement");
    formData.append("header_accent", headerAccent);
    formData.append("include_closing", includeClosing ? "true" : "false");
    formData.append("brand", brand);
    formData.append("message", toEmailHtml(message));
    formData.append("include_parents", "true");

    for (const [name, value] of recipientFields({
      recipientType,
      selectedClassIds,
      selectedProgramId,
      selectedAreaId,
      programAreaId,
      areaProgramId,
      customRecipient,
    })) {
      formData.append(name, value);
    }

    for (const att of attachments) {
      formData.append("attachments", att.file);
    }

    const cidIds = [];
    for (const img of inlineImages) {
      formData.append("inline_images", img.file);
      cidIds.push(img.id);
    }
    if (cidIds.length > 0) {
      formData.append("inline_image_ids", JSON.stringify(cidIds));
    }
    return formData;
  };

  const confirmSchedule = async () => {
    setConfirmDialog({ isOpen: false });
    setIsSending(true);
    try {
      const formData = buildEmailForm();
      // The composer's own text, so the email can be edited later.
      formData.append("message_source", message);
      for (const [name, value] of scheduleFields(schedule)) {
        formData.append(name, value);
      }
      const created = await adminService.scheduleBulkEmail(formData);
      const first = created?.next_run_at
        ? new Date(created.next_run_at).toLocaleString("en-US", {
            timeZone: "America/New_York",
            weekday: "short",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        : null;
      toast.success(
        first ? `Scheduled. First send ${first} (Eastern).` : "Email scheduled",
      );
      setSchedule(defaultSchedule());
      setSendMode("now");
    } catch (error) {
      console.error("Failed to schedule bulk email:", error);
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to schedule the email";
      toast.error(typeof msg === "string" ? msg : "Failed to schedule the email");
    } finally {
      setIsSending(false);
    }
  };

  const confirmSend = async () => {
    setConfirmDialog({ isOpen: false });
    setIsSending(true);
    setSendResult(null);

    try {
      const formData = buildEmailForm();

      const result = await adminService.sendBulkEmail(formData);
      setSendResult(result);

      if (result.successful > 0) {
        toast.success(`Email sent to ${result.successful} recipient(s)`);
      }
      if (result.failed > 0) {
        toast.error(`${result.failed} email(s) failed to send`);
      }
    } catch (error) {
      console.error("Failed to send bulk email:", error);
      const msg =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to send emails";
      toast.error(typeof msg === "string" ? msg : "Failed to send emails");
    } finally {
      setIsSending(false);
    }
  };

  // The search box is the answer to "it takes very long to find a class".
  const visibleClasses = classSearch.trim()
    ? classes.filter((c) =>
        (c.name || "").toLowerCase().includes(classSearch.trim().toLowerCase()),
      )
    : classes;

  const toggleClass = (id) =>
    setSelectedClassIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );

  const getRecipientLabel = () => {
    switch (recipientType) {
      case "all":
        return "all parents with active enrollments";
      case "all_accounts":
        return "all account holders";
      case "class": {
        const picked = classes.filter((c) => selectedClassIds.includes(c.id));
        if (picked.length === 0) return "selected class parents";
        if (picked.length === 1) return `parents in "${picked[0].name}"`;
        return `parents across ${picked.length} classes`;
      }
      case "program": {
        const prog = programs.find((p) => p.id === selectedProgramId);
        const area = areas.find((a) => a.id === programAreaId);
        if (!prog) return "selected program parents";
        return area
          ? `parents in "${prog.name}" in "${area.name}"`
          : `parents in "${prog.name}" (all areas)`;
      }
      case "area": {
        const area = areas.find((a) => a.id === selectedAreaId);
        const prog = programs.find((p) => p.id === areaProgramId);
        if (!area) return "selected area parents";
        return prog
          ? `parents in "${prog.name}" in "${area.name}"`
          : `parents in "${area.name}" (all programs)`;
      }
      case "custom":
        return customRecipient
          ? `${customRecipient.name || customRecipient.email} (${customRecipient.email})`
          : "selected recipient";
      default:
        return "selected recipients";
    }
  };

  const handleReset = () => {
    setSubject("");
    setMessage("");
    setSendResult(null);
    setRecipientType("all");
    setSelectedClassIds([]);
    setClassSearch("");
    setSelectedProgramId("");
    setSelectedAreaId("");
    setProgramAreaId("");
    setAreaProgramId("");
    inlineImages.forEach((img) => {
      if (img.previewUrl) URL.revokeObjectURL(img.previewUrl);
    });
    setAttachments([]);
    setInlineImages([]);
  };

  const inputStyle =
    "w-full p-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F3BC48] focus:border-transparent bg-white font-manrope";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header />

      <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 flex-1 overflow-y-auto w-full">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl md:text-[30px] lg:text-[46px] font-bold text-text-primary font-kollektif flex items-center gap-3">
            <Mail className="w-8 h-8 text-btn-gold shrink-0 hidden sm:block" />
            Mass Email
          </h1>
          <p className="text-xs sm:text-sm text-neutral-main font-manrope mt-1">
            Send emails to parents by class, program, area, or all at once
          </p>
        </div>

        {/* Send Result */}
        {sendResult && (
          <div
            className={`mb-6 p-4 rounded-lg border ${sendResult.failed > 0 ? "bg-orange-50 border-orange-200" : "bg-green-50 border-green-200"}`}
          >
            <div className="flex items-start gap-3">
              {sendResult.failed > 0 ? (
                <AlertCircle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
              )}
              <div>
                <h3 className="font-semibold font-manrope text-sm text-gray-800">
                  Email Sent
                </h3>
                <p className="text-sm font-manrope text-gray-600 mt-1">
                  {sendResult.successful} of {sendResult.total_recipients}{" "}
                  emails sent successfully.
                  {sendResult.failed > 0 && ` ${sendResult.failed} failed.`}
                </p>
                <button
                  onClick={handleReset}
                  className="mt-3 text-sm font-semibold text-[#173151] underline font-manrope"
                >
                  Send Another Email
                </button>
              </div>
            </div>
          </div>
        )}

        {!sendResult && (
          <div className="space-y-6">
            {/* Recipient Selection */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h2 className="text-base font-semibold font-manrope text-text-primary mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-btn-gold" />
                Recipients
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                {RECIPIENT_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setRecipientType(type.value);
                      setSelectedClassIds([]);
                      setClassSearch("");
                      setSelectedProgramId("");
                      setSelectedAreaId("");
                      setProgramAreaId("");
                      setAreaProgramId("");
                    }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      recipientType === type.value
                        ? "border-[#F3BC48] bg-[#F3BC48]/10"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <p className="font-semibold text-sm font-manrope text-text-primary">
                      {type.label}
                    </p>
                    <p className="text-xs text-text-muted font-manrope mt-0.5">
                      {type.description}
                    </p>
                  </button>
                ))}
              </div>

              {/* Dynamic dropdown based on recipient type */}
              {recipientType === "custom" && customRecipient && (
                <div className="mt-4 flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
                  <div className="text-sm font-manrope">
                    Sending to:{" "}
                    <span className="font-semibold">
                      {customRecipient.name || customRecipient.email}
                    </span>{" "}
                    <span className="text-gray-500">({customRecipient.email})</span>
                  </div>
                  <button
                    type="button"
                    className="text-sm text-red-600 hover:underline"
                    onClick={() => {
                      setCustomRecipient(null);
                      setRecipientType("all");
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}

              {recipientType === "class" && (
                <div>
                  <label className="text-sm font-medium text-gray-700 font-manrope">
                    Select Class *
                  </label>
                  <div className="flex flex-wrap items-center gap-2 mt-2 mb-2">
                    {[
                      { value: "", label: "All" },
                      { value: "active", label: "Active" },
                      { value: "upcoming", label: "Upcoming" },
                      { value: "completed", label: "Completed" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setClassFilter(opt.value);
                          setSelectedClassIds([]);
                        }}
                        className={`px-4 py-1.5 rounded-full text-sm font-manrope font-medium border transition-colors ${
                          classFilter === opt.value
                            ? "bg-[#173151] text-white border-[#173151]"
                            : "bg-white text-gray-600 border-gray-300 hover:border-[#173151]"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="relative mb-2">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      id="class-search"
                      type="text"
                      value={classSearch}
                      onChange={(e) => setClassSearch(e.target.value)}
                      placeholder="Search classes by name"
                      disabled={loadingOptions}
                      className={`${inputStyle} pl-9`}
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-xs text-gray-500 font-manrope">
                      {loadingOptions
                        ? "Loading classes..."
                        : `${selectedClassIds.length} of ${visibleClasses.length} selected`}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedClassIds((prev) =>
                            Array.from(
                              new Set([
                                ...prev,
                                ...visibleClasses.map((c) => c.id),
                              ]),
                            ),
                          )
                        }
                        disabled={loadingOptions || visibleClasses.length === 0}
                        className="text-xs font-semibold text-[#173151] underline font-manrope disabled:opacity-40"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedClassIds([])}
                        disabled={selectedClassIds.length === 0}
                        className="text-xs font-semibold text-gray-500 underline font-manrope disabled:opacity-40"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-300 bg-white divide-y divide-gray-100">
                    {visibleClasses.length === 0 ? (
                      <p className="p-3 text-sm text-gray-500 font-manrope">
                        {loadingOptions
                          ? "Loading classes..."
                          : classSearch
                            ? `No classes match "${classSearch}"`
                            : "No classes found"}
                      </p>
                    ) : (
                      visibleClasses.map((cls) => (
                        <label
                          key={cls.id}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={selectedClassIds.includes(cls.id)}
                            onChange={() => toggleClass(cls.id)}
                            className="w-4 h-4 rounded border-gray-300 text-[#173151] focus:ring-[#173151]"
                          />
                          <span className="text-sm text-gray-800 font-manrope">
                            {cls.name}
                          </span>
                        </label>
                      ))
                    )}
                  </div>

                  {selectedClassIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {classes
                        .filter((c) => selectedClassIds.includes(c.id))
                        .map((cls) => (
                          <span
                            key={cls.id}
                            className="inline-flex items-center gap-1.5 bg-[#173151]/10 text-[#173151] text-xs font-medium font-manrope px-2.5 py-1 rounded-full"
                          >
                            {cls.name}
                            <button
                              type="button"
                              onClick={() => toggleClass(cls.id)}
                              aria-label={`Remove ${cls.name}`}
                              className="hover:text-[#0f1d2e]"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {recipientType === "program" && (
                <div>
                  <label className="text-sm font-medium text-gray-700 font-manrope">
                    Select Program *
                  </label>
                  <select
                    value={selectedProgramId}
                    onChange={(e) => setSelectedProgramId(e.target.value)}
                    className={inputStyle}
                    disabled={loadingOptions}
                  >
                    <option value="">
                      {loadingOptions
                        ? "Loading programs..."
                        : "Choose a program"}
                    </option>
                    {programs.map((prog) => (
                      <option key={prog.id} value={prog.id}>
                        {prog.name}
                      </option>
                    ))}
                  </select>
                  <label className="text-sm font-medium text-gray-700 font-manrope mt-3 block">
                    Only in area
                  </label>
                  <select
                    aria-label="Only in area"
                    value={programAreaId}
                    onChange={(e) => setProgramAreaId(e.target.value)}
                    className={inputStyle}
                    disabled={loadingOptions}
                  >
                    <option value="">All areas</option>
                    {areas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {recipientType === "area" && (
                <div>
                  <label className="text-sm font-medium text-gray-700 font-manrope">
                    Select Area *
                  </label>
                  <select
                    value={selectedAreaId}
                    onChange={(e) => setSelectedAreaId(e.target.value)}
                    className={inputStyle}
                    disabled={loadingOptions}
                  >
                    <option value="">
                      {loadingOptions ? "Loading areas..." : "Choose an area"}
                    </option>
                    {areas.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.name}
                      </option>
                    ))}
                  </select>
                  <label className="text-sm font-medium text-gray-700 font-manrope mt-3 block">
                    Only in program
                  </label>
                  <select
                    aria-label="Only in program"
                    value={areaProgramId}
                    onChange={(e) => setAreaProgramId(e.target.value)}
                    className={inputStyle}
                    disabled={loadingOptions}
                  >
                    <option value="">All programs</option>
                    {programs.map((prog) => (
                      <option key={prog.id} value={prog.id}>
                        {prog.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Email Templates */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-base font-semibold font-manrope text-text-primary flex items-center gap-2">
                  <FileText className="w-5 h-5 text-btn-gold" />
                  Email Templates
                </h2>
                <button
                  type="button"
                  onClick={() => setShowSaveTemplate((v) => !v)}
                  className="text-sm font-semibold text-[#173151] bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 font-manrope"
                >
                  {showSaveTemplate ? "Cancel" : "Save current as template"}
                </button>
              </div>
              <p className="text-xs text-text-muted font-manrope mb-3">
                Start from a ready-made template or one you saved — then edit
                freely below. Templates fill in the subject and message.
              </p>

              {showSaveTemplate && (
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Template name (e.g. Rainout notice)"
                    maxLength={120}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold"
                  />
                  <button
                    type="button"
                    onClick={handleSaveTemplate}
                    disabled={savingTemplate}
                    className="px-4 py-2 bg-[#173151] text-white rounded-lg text-sm font-semibold font-manrope disabled:opacity-50"
                  >
                    {savingTemplate ? "Saving…" : "Save"}
                  </button>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={clearTemplate}
                  className={`px-3 py-2 rounded-lg border text-left font-manrope text-sm transition-colors ${
                    activeTemplateId === ""
                      ? "border-[#173151] bg-[#173151]/5"
                      : "border-gray-200 hover:border-[#173151]"
                  }`}
                >
                  <span className="font-semibold block">Blank</span>
                  <span className="text-xs text-text-muted">Start from scratch</span>
                </button>
                {BUILTIN_EMAIL_TEMPLATES.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    className={`px-3 py-2 rounded-lg border text-left font-manrope text-sm transition-colors ${
                      activeTemplateId === tpl.id
                        ? "border-[#173151] bg-[#173151]/5"
                        : "border-gray-200 hover:border-[#173151]"
                    }`}
                  >
                    <span className="font-semibold block">{tpl.name}</span>
                    <span className="text-xs text-text-muted">{tpl.description}</span>
                  </button>
                ))}
                {savedTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className={`relative pr-8 px-3 py-2 rounded-lg border text-left font-manrope text-sm transition-colors cursor-pointer ${
                      activeTemplateId === tpl.id
                        ? "border-[#173151] bg-[#173151]/5"
                        : "border-gray-200 hover:border-[#173151]"
                    }`}
                    onClick={() => applyTemplate(tpl)}
                  >
                    <span className="font-semibold block">{tpl.name}</span>
                    <span className="text-xs text-text-muted">My template</span>
                    <button
                      type="button"
                      title="Delete template"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(tpl.id);
                      }}
                      className="absolute right-1.5 top-1.5 p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Email Composer */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              {/* Appearance: header label, header color, closing lines */}
              <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-gray-100">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="mass-email-brand"
                    className="text-xs text-text-muted font-manrope"
                  >
                    Send as
                  </label>
                  <select
                    id="mass-email-brand"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-manrope focus:outline-none focus:ring-2 focus:ring-btn-gold"
                  >
                    {Object.entries(EMAIL_BRANDS).map(([key, b]) => (
                      <option key={key} value={key}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-text-muted font-manrope">
                    Header label
                  </label>
                  <input
                    type="text"
                    value={headerSubtitle}
                    onChange={(e) => setHeaderSubtitle(e.target.value)}
                    maxLength={60}
                    className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-manrope w-44 focus:outline-none focus:ring-2 focus:ring-btn-gold"
                    placeholder="Announcement"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-text-muted font-manrope">
                    Header color
                  </label>
                  <div className="flex items-center gap-1.5">
                    {[
                      { c: "#173151", label: "Navy" },
                      { c: "#b8860b", label: "Gold" },
                      { c: "#c0392b", label: "Red" },
                      { c: "#1e7e34", label: "Green" },
                    ].map(({ c, label }) => (
                      <button
                        key={c}
                        type="button"
                        title={label}
                        onClick={() => setHeaderAccent(c)}
                        className={`w-7 h-7 rounded-full border-2 ${
                          headerAccent === c
                            ? "border-btn-gold ring-2 ring-btn-gold/40"
                            : "border-white shadow"
                        }`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm font-manrope text-text-primary pb-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeClosing}
                    onChange={(e) => setIncludeClosing(e.target.checked)}
                    className="w-4 h-4 accent-[#173151]"
                  />
                  Add standard closing ("questions… best regards")
                </label>
                <span className="text-xs text-text-muted font-manrope pb-2">
                  📷 Tip: the toolbar's image icon embeds photos right in the
                  email
                </span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold font-manrope text-text-primary flex items-center gap-2">
                  <Mail className="w-5 h-5 text-btn-gold" />
                  Compose Email
                </h2>
                <button
                  type="button"
                  onClick={() => setShowPreview(true)}
                  disabled={!subject.trim() || !hasMessageContent}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#173151] bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-manrope disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 font-manrope">
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className={inputStyle}
                    placeholder="Email subject line"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 font-manrope mb-1 block">
                    Message *
                  </label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#F3BC48] focus-within:border-transparent">
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={message}
                      onChange={setMessage}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Write your message here..."
                      className="mass-email-editor"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 font-manrope flex items-center gap-1.5">
                      <Paperclip className="w-4 h-4" />
                      Attachments
                      <span className="text-xs text-text-muted font-normal">
                        ({totalFileCount}/{MAX_FILE_COUNT} files,{" "}
                        {formatFileSize(totalFileSize)}/
                        {formatFileSize(MAX_TOTAL_SIZE)})
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        totalFileCount >= MAX_FILE_COUNT ||
                        totalFileSize >= MAX_TOTAL_SIZE
                      }
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#173151] bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-manrope disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Paperclip className="w-4 h-4" />
                      Add File
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.xlsx"
                      onChange={handleAttachmentAdd}
                      multiple
                    />
                  </div>

                  {(attachments.length > 0 || inlineImages.length > 0) && (
                    <div className="space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      {attachments.map((att) => (
                        <div
                          key={att.id}
                          className="flex items-center justify-between text-sm font-manrope"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="truncate text-text-primary">
                              {att.name}
                            </span>
                            <span className="text-text-muted shrink-0">
                              ({formatFileSize(att.size)})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(att.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors shrink-0"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        </div>
                      ))}
                      {inlineImages.map((img) => (
                        <div
                          key={img.id}
                          className="flex items-center justify-between text-sm font-manrope"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Image className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                            <span className="truncate text-text-primary">
                              {img.name}
                            </span>
                            <span className="text-text-muted shrink-0">
                              (inline, {formatFileSize(img.size)})
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeInlineImage(img.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors shrink-0"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* When to send */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
              <h2 className="text-base font-semibold font-manrope text-text-primary">
                When to send
              </h2>
              <div className="flex flex-wrap gap-6 text-sm font-manrope">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="send-mode"
                    checked={sendMode === "now"}
                    onChange={() => setSendMode("now")}
                  />
                  Send now
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="send-mode"
                    checked={sendMode === "later"}
                    onChange={() => setSendMode("later")}
                  />
                  Schedule for later
                </label>
              </div>
              {sendMode === "later" && (
                <ScheduleEditor value={schedule} onChange={setSchedule} />
              )}
            </div>

            {/* Send Button */}
            <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="text-sm font-manrope text-text-muted">
                Sending to:{" "}
                <strong className="text-text-primary">
                  {getRecipientLabel()}
                </strong>
              </div>
              <button
                onClick={handleSend}
                disabled={
                  isSending ||
                  !subject.trim() ||
                  !hasMessageContent ||
                  Boolean(scheduleProblem)
                }
                className="flex items-center gap-2 px-6 py-3 bg-[#F3BC48] hover:bg-[#e5a920] text-[#173151] font-semibold rounded-lg transition-colors shadow-sm font-manrope disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {sendMode === "later" ? "Scheduling..." : "Sending..."}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    {sendMode === "later" ? "Schedule Email" : "Send Email"}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant="warning"
      />

      {/* Email Preview Modal */}
      {showPreview &&
        (() => {
          const pickedClasses =
            recipientType === "class"
              ? classes.filter((c) => selectedClassIds.includes(c.id))
              : [];
          const selectedClassName = regardingPreview(pickedClasses);

          // Replace cid: references with local preview URLs for the preview
          let previewMessage = toEmailHtml(message);
          for (const img of inlineImages) {
            if (img.previewUrl) {
              previewMessage = previewMessage.replace(
                `src="cid:${img.id}"`,
                `src="${img.previewUrl}"`,
              );
            }
          }

          return (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4"
              onClick={() => setShowPreview(false)}
            >
              <div
                role="dialog"
                aria-label="Email preview"
                className="bg-[#f3f6fb] rounded-xl max-w-[640px] w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Preview Header */}
                <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-white">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-base sm:text-lg text-heading-dark font-manrope flex items-center gap-1.5 sm:gap-2">
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-btn-gold shrink-0" />
                      Email Preview
                    </h3>
                    <p className="text-xs text-text-muted font-manrope mt-0.5">
                      This is how the email will appear to recipients
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="p-1.5 sm:p-2 hover:bg-gray-200 rounded-lg transition-colors shrink-0"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                  </button>
                </div>

                {/* Subject Bar */}
                <div className="px-4 py-2 bg-white border-b border-gray-200">
                  <p className="text-xs text-text-muted font-manrope">
                    Subject
                  </p>
                  <p className="text-sm font-semibold text-text-primary font-manrope">
                    {subject || "(No subject)"}
                  </p>
                </div>

                {/* Email Render */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-5">
                  <div
                    style={{
                      maxWidth: 600,
                      margin: "0 auto",
                      fontFamily: "'Segoe UI', Arial, sans-serif",
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        background: `linear-gradient(135deg, ${headerAccent} 0%, ${headerAccent} 100%)`,
                        color: "white",
                        padding: "30px 20px",
                        textAlign: "center",
                        borderRadius: "10px 10px 0 0",
                      }}
                    >
                      {sender.logo && (
                        <div
                          style={{
                            backgroundColor: "#ffffff",
                            display: "inline-block",
                            padding: "10px 14px",
                            borderRadius: 8,
                            marginBottom: 12,
                          }}
                        >
                          <img
                            src={sender.logo}
                            alt={sender.name}
                            width={120}
                            style={{ display: "block", width: 120, height: "auto" }}
                          />
                        </div>
                      )}
                      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                        {sender.name}
                      </h1>
                      <p
                        style={{
                          margin: "5px 0 0 0",
                          fontSize: 14,
                          opacity: 0.9,
                        }}
                      >
                        {headerSubtitle || "Announcement"}
                      </p>
                    </div>

                    {/* Content */}
                    <div
                      style={{
                        backgroundColor: "#ffffff",
                        padding: 30,
                        border: "1px solid #dee5f2",
                        borderRadius: "0 0 10px 10px",
                        lineHeight: 1.6,
                        color: "#173151",
                      }}
                    >
                      <p style={{ margin: "0 0 16px 0" }}>
                        Hi <strong>Parent Name</strong>,
                      </p>

                      <div
                        dangerouslySetInnerHTML={{ __html: previewMessage }}
                      />

                      {selectedClassName && (
                        <div
                          style={{
                            backgroundColor: "#f3f6fb",
                            padding: 20,
                            margin: "20px 0",
                            borderRadius: 8,
                            borderLeft: "4px solid #F3BC48",
                          }}
                        >
                          <p style={{ margin: 0 }}>
                            <strong>Regarding:</strong> {selectedClassName}
                          </p>
                        </div>
                      )}

                      {includeClosing && (
                        <>
                          <p style={{ margin: "16px 0 0 0" }}>
                            {brand === "csf" ? (
                              "If you have any questions, please don't hesitate to contact us."
                            ) : (
                              <>
                                If you have any questions, please don't
                                hesitate to reach out to us at{" "}
                                <a href={`mailto:${sender.email}`}>
                                  {sender.email}
                                </a>
                                .
                              </>
                            )}
                          </p>
                          <p style={{ margin: "16px 0 0 0" }}>
                            Best regards,
                            <br />
                            <strong>{`${sender.name} Team`}</strong>
                          </p>
                        </>
                      )}
                    </div>

                    {/* Footer */}
                    <div
                      style={{
                        textAlign: "center",
                        marginTop: 30,
                        padding: 20,
                        color: "#666",
                        fontSize: "0.85em",
                      }}
                    >
                      <p style={{ margin: 0 }}>
                        {sender.name}
                        {sender.website && (
                          <>
                            <br />
                            <a
                              href="#"
                              style={{ color: "#173151" }}
                              onClick={(e) => e.preventDefault()}
                            >
                              {sender.website}
                            </a>
                          </>
                        )}
                      </p>
                      <hr
                        style={{
                          border: "none",
                          borderTop: "1px solid #dee5f2",
                          margin: "20px 0",
                        }}
                      />
                      <p style={{ margin: 0 }}>
                        This is an automated email. Please do not reply directly
                        to this message.
                        <br />
                        If you have questions, contact us at{" "}
                        <a
                          href="#"
                          style={{ color: "#173151" }}
                          onClick={(e) => e.preventDefault()}
                        >
                          {sender.email}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview Footer */}
                <div className="p-3 sm:p-4 border-t bg-white flex items-center justify-between">
                  <div>
                    <p className="text-xs text-text-muted font-manrope">
                      Sending to:{" "}
                      <strong className="text-text-primary">
                        {getRecipientLabel()}
                      </strong>
                    </p>
                    {(attachments.length > 0 || inlineImages.length > 0) && (
                      <p className="text-xs text-text-muted font-manrope mt-1">
                        <Paperclip className="w-3 h-3 inline mr-1" />
                        {attachments.length + inlineImages.length} file(s)
                        attached ({formatFileSize(totalFileSize)})
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-manrope"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Quill editor styles */}
      <style>{`
        .mass-email-editor .ql-toolbar {
          border: none;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
          border-radius: 0;
        }
        .mass-email-editor .ql-container {
          border: none;
          font-family: 'Manrope', sans-serif;
          font-size: 14px;
          min-height: 200px;
        }
        .mass-email-editor .ql-editor {
          min-height: 200px;
          line-height: 1.6;
          color: #173151;
        }
        .mass-email-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .mass-email-editor .ql-snow .ql-stroke {
          stroke: #6b7280;
        }
        .mass-email-editor .ql-snow .ql-fill {
          fill: #6b7280;
        }
        .mass-email-editor .ql-snow .ql-picker {
          color: #6b7280;
        }
        .mass-email-editor .ql-snow button:hover .ql-stroke,
        .mass-email-editor .ql-snow .ql-picker-label:hover .ql-stroke {
          stroke: #F3BC48;
        }
        .mass-email-editor .ql-snow button:hover .ql-fill,
        .mass-email-editor .ql-snow .ql-picker-label:hover .ql-fill {
          fill: #F3BC48;
        }
        .mass-email-editor .ql-snow button.ql-active .ql-stroke {
          stroke: #F3BC48;
        }
        .mass-email-editor .ql-snow button.ql-active .ql-fill {
          fill: #F3BC48;
        }
      `}</style>
    </div>
  );
}
